/**
 * New IdP Connection page - step wizard for creating a connection
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useCreateIdPConnection } from '@/hooks/use-idp-connections'
import type { IdPProviderType, CreateIdPConnectionRequest } from '@/types/uas'

const STEPS = ['Provider', 'Configuration', 'Mapping', 'Schedule'] as const

const PROVIDER_OPTIONS: Array<{ value: IdPProviderType; label: string; description: string }> = [
  { value: 'LDAP', label: 'LDAP / Active Directory', description: 'Connect to on-premise directory services' },
  { value: 'AZURE_AD', label: 'Microsoft Entra ID', description: 'Connect via MS Graph API with OAuth2' },
  { value: 'OKTA', label: 'Okta', description: 'Connect to Okta Universal Directory' },
  { value: 'GOOGLE_WORKSPACE', label: 'Google Workspace', description: 'Connect to Google Workspace directory' },
  { value: 'CUSTOM_REST_API', label: 'Custom REST API', description: 'Connect to any REST-based user directory' },
]

const LDAP_FIELDS = [
  { key: 'url', label: 'LDAP URL', placeholder: 'ldap://ldap.example.com:389' },
  { key: 'bindDn', label: 'Bind DN', placeholder: 'cn=admin,dc=example,dc=com' },
  { key: 'bindPassword', label: 'Bind Password', placeholder: 'password', type: 'password' },
  { key: 'searchBase', label: 'Search Base', placeholder: 'ou=people,dc=example,dc=com' },
  { key: 'searchFilter', label: 'Search Filter', placeholder: '(objectClass=person)' },
]

const AZURE_FIELDS = [
  { key: 'tenantId', label: 'Azure Tenant ID', placeholder: 'xxxxxxxx-xxxx-...' },
  { key: 'clientId', label: 'Client ID', placeholder: 'xxxxxxxx-xxxx-...' },
  { key: 'clientSecret', label: 'Client Secret', placeholder: 'secret', type: 'password' },
]

const OKTA_FIELDS = [
  { key: 'domain', label: 'Okta Domain', placeholder: 'dev-12345.okta.com' },
  { key: 'apiToken', label: 'API Token', placeholder: 'SSWS ...', type: 'password' },
]

export function ConnectionNewPage() {
  const navigate = useNavigate()
  const createConnection = useCreateIdPConnection()

  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [providerType, setProviderType] = useState<IdPProviderType | null>(null)
  const [config, setConfig] = useState<Record<string, string>>({})
  const [mappingText, setMappingText] = useState('mail=subject.email\ndisplayName=subject.display_name\ndepartment=subject.department')
  const [syncEnabled, setSyncEnabled] = useState(false)
  const [syncSchedule, setSyncSchedule] = useState('0 2 * * *')

  const updateConfig = (key: string, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }))
  }

  const parseMapping = (): Record<string, string> => {
    const result: Record<string, string> = {}
    for (const line of mappingText.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed || !trimmed.includes('=')) continue
      const [source, target] = trimmed.split('=', 2)
      if (source && target) {
        result[source.trim()] = target.trim()
      }
    }
    return result
  }

  const handleSubmit = () => {
    if (!providerType || !name.trim()) return

    const data: CreateIdPConnectionRequest = {
      name,
      description: description || undefined,
      providerType,
      config,
      attributeMapping: parseMapping(),
      syncEnabled,
      syncSchedule: syncEnabled ? syncSchedule : undefined,
    }

    createConnection.mutate(data)
  }

  const configFields = providerType === 'LDAP'
    ? LDAP_FIELDS
    : providerType === 'AZURE_AD'
      ? AZURE_FIELDS
      : providerType === 'OKTA'
        ? OKTA_FIELDS
        : [{ key: 'baseUrl', label: 'Base URL', placeholder: 'https://api.example.com' }]

  const canNext = () => {
    if (step === 0) return !!providerType && name.trim().length > 0
    if (step === 1) return Object.values(config).some((v) => v.length > 0)
    if (step === 2) return mappingText.trim().length > 0
    return true
  }

  return (
    <>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Button variant="ghost" size="sm" onClick={() => navigate('/identity/connections')}>
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          Connections
        </Button>
        <Separator orientation="vertical" className="h-4" />
        <h1 className="text-base font-medium">New IdP Connection</h1>
      </header>

      <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 max-w-2xl">
        {/* Steps indicator */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium ${
                  i < step
                    ? 'bg-primary text-primary-foreground'
                    : i === step
                      ? 'border-2 border-primary text-primary'
                      : 'border border-muted-foreground/30 text-muted-foreground'
                }`}
              >
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className={`text-sm ${i === step ? 'font-medium' : 'text-muted-foreground'}`}>
                {s}
              </span>
              {i < STEPS.length - 1 && (
                <Separator className="w-8" />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: Provider Selection */}
        {step === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Provider</CardTitle>
              <CardDescription>Choose the identity provider type and name this connection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Connection Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Corporate AD"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Main corporate Active Directory"
                />
              </div>
              <div>
                <Label>Provider Type</Label>
                <div className="mt-2 grid gap-2">
                  {PROVIDER_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      className={`cursor-pointer rounded-md border p-3 transition-[border-color,box-shadow,background-color] ${
                        providerType === opt.value
                          ? 'border-primary bg-primary/5'
                          : 'hover:border-primary/30'
                      }`}
                      onClick={() => setProviderType(opt.value)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setProviderType(opt.value)
                      }}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Configuration */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Connection Configuration</CardTitle>
              <CardDescription>Enter credentials and connection details (stored encrypted)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configFields.map((field) => (
                <div key={field.key}>
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type ?? 'text'}
                    value={config[field.key] ?? ''}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Attribute Mapping */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Attribute Mapping</CardTitle>
              <CardDescription>Map IdP fields to poli-x attribute keys (one per line: source=target)</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={mappingText}
                onChange={(e) => setMappingText(e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder="mail=subject.email&#10;displayName=subject.display_name&#10;department=subject.department"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 3: Sync Schedule */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Sync Schedule</CardTitle>
              <CardDescription>Configure automatic synchronization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Automatic Sync</Label>
                  <p className="text-xs text-muted-foreground">Periodically sync users from IdP</p>
                </div>
                <Switch checked={syncEnabled} onCheckedChange={setSyncEnabled} />
              </div>
              {syncEnabled && (
                <div>
                  <Label>Cron Schedule</Label>
                  <Input
                    value={syncSchedule}
                    onChange={(e) => setSyncSchedule(e.target.value)}
                    placeholder="0 2 * * *"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Default: 2:00 AM daily
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext()}
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={createConnection.isPending}
            >
              {createConnection.isPending ? 'Creating...' : 'Create Connection'}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
